import { Toggle } from '@carbon/react';
import { NumberField } from 'components/form-fields-2';
import type { ExpenseAreaConfig } from '../types';
import styles from './economic-limits-section.module.scss';

interface EconomicLimitsSectionProps {
  area: ExpenseAreaConfig;
  onChange: (patch: Partial<ExpenseAreaConfig>) => void;
}

/**
 * Per-area economic limit settings. When applied, a property whose monthly
 * cashflow falls below the limit drops out of the evaluation; reactivation
 * brings it back once it has been economic again for a run of months.
 *
 * Toggling the limit off hides the dependent inputs rather than clearing them
 * here — the server clears the whole group on save, so a user who toggles off
 * and on again mid-edit keeps what they typed, and nothing stale persists.
 * @param {EconomicLimitsSectionProps} props - The area and change handler
 * @return {JSX.Element} The economic limits fields
 */
export function EconomicLimitsSection({
  area,
  onChange,
}: EconomicLimitsSectionProps): JSX.Element {
  return (
    <div className={styles.econLimits}>
      <Toggle
        id={`econ-limit-${area.id}`}
        labelText="Economic limit"
        labelA="Not applied"
        labelB="Applied"
        toggled={area.economicLimitApplied}
        onToggle={(checked: boolean) =>
          onChange({ economicLimitApplied: checked })
        }
      />

      {area.economicLimitApplied && (
        <>
          <div className={styles.field}>
            <NumberField
              id={`econ-limit-amount-${area.id}`}
              label="Limit amount ($/month)"
              prefix="$"
              thousandSeparator
              allowNegative={false}
              value={area.economicLimitAmount}
              onChange={(value) => onChange({ economicLimitAmount: value })}
            />
          </div>

          <Toggle
            id={`econ-reactivate-${area.id}`}
            labelText="Bring property back into the evaluation if it becomes economic again"
            labelA="Off"
            labelB="On"
            toggled={area.economicLimitReactivate}
            onToggle={(checked: boolean) =>
              onChange({ economicLimitReactivate: checked })
            }
          />

          {area.economicLimitReactivate && (
            <div className={styles.field}>
              <NumberField
                id={`econ-reactivate-months-${area.id}`}
                label="Consecutive months back to economic"
                allowNegative={false}
                decimalScale={0}
                value={area.economicLimitReactivateMonths}
                onChange={(value) =>
                  onChange({ economicLimitReactivateMonths: value })
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
